/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 날짜포맷 클래스를 정의한다.
 *
 * @file /src/utils/FormatDate.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 10. 14.
 */
export class FormatDate {
    /**
     * 날짜객체
     */
    date: Date;

    /**
     * 언어
     */
    language: string = null;

    /**
     * 날짜포맷 클래스를 정의한다.
     *
     * @param date - 초기날짜 (unixtime, 포맷팅된 날짜, Date 객체)
     */
    constructor(date: number | string | Date = null) {
        if (date === null || date === undefined) {
            this.date = new Date();
        } else if (date instanceof Date) {
            this.date = new Date(date.getTime());
        } else if (typeof date === 'number') {
            this.date = this.parseNumber(date);
        } else {
            this.date = this.parseString(date);
        }
    }

    /**
     * 영문 월 이름
     */
    static MONTH_NAMES: string[] = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

    /**
     * 년/월/일/시/분/초 값으로 실제 존재하는 날짜인 경우에만 Date 객체를 생성한다.
     *
     * @param year - 년도
     * @param month - 월 (1 ~ 12)
     * @param day - 일
     * @param time - [시, 분, 초, 밀리초]
     * @returns Date 객체 (존재하지 않는 날짜일 경우 null)
     */
    makeDate(year: number, month: number, day: number, time: number[] = [0, 0, 0, 0]): Date {
        const [hour = 0, minute = 0, second = 0, ms = 0] = time;
        if (month < 1 || month > 12 || day < 1 || day > 31) return null;
        if (hour > 24 || minute > 59 || second > 59) return null;

        const date = new Date(year, month - 1, day, hour, minute, second, ms);
        // 0 ~ 99 년도가 1900 년대로 보정되는 것을 방지한다.
        date.setFullYear(year, month - 1, day);

        if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
            return null;
        }

        return date;
    }

    /**
     * 2자리 년도를 4자리 년도로 변환한다.
     * 현재년도 + 20년 까지는 2000년대, 그 이후는 1900년대로 판단한다.
     *
     * @param year - 년도
     * @returns 4자리 년도
     */
    toFullYear(year: number): number {
        if (year >= 100) return year;
        const pivot = (new Date().getFullYear() % 100) + 20;
        return year <= pivot ? 2000 + year : 1900 + year;
    }

    /**
     * 현재 언어에서 일/월/년 표기 순서를 가져온다.
     *
     * @returns 표기순서 (예: ['y', 'm', 'd'], ['m', 'd', 'y'], ['d', 'm', 'y'])
     */
    getDateOrder(): string[] {
        try {
            const language =
                this.language || (typeof document !== 'undefined' ? document.documentElement?.lang : null) || undefined;
            const parts = new Intl.DateTimeFormat(language, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }).formatToParts(new Date(2000, 10, 22));
            const order = parts
                .filter((part) => ['year', 'month', 'day'].includes(part.type))
                .map((part) => part.type.charAt(0));
            if (order.length === 3) return order;
        } catch {}

        return ['y', 'm', 'd'];
    }

    /**
     * 숫자형태의 초기날짜를 해석한다.
     * 20201010 (YYYYMMDD), 202010101230 (YYYYMMDDHHmm), 20201010123000 (YYYYMMDDHHmmss) 형태를 우선 해석하고,
     * 그 외에는 unix time stamp 로 판단한다.
     *
     * @param num - 초기날짜
     * @returns Date 객체
     */
    parseNumber(num: number): Date {
        if (Number.isInteger(num) === true && num > 0) {
            const digits = num.toString();
            if ([8, 12, 14].includes(digits.length) === true) {
                const date = this.parseDigits(digits);
                if (date !== null) return date;
            }
        }

        return new Date(this.toMilliseconds(num));
    }

    /**
     * 숫자로만 이루어진 문자열을 해석한다.
     *
     * @param digits - 숫자 문자열
     * @returns Date 객체 (해석할 수 없는 경우 null)
     */
    parseDigits(digits: string): Date {
        const n = (start: number, length: number): number => Number(digits.substring(start, start + length));
        const order = this.getDateOrder();

        switch (digits.length) {
            // D or DD (이번달)
            case 1:
            case 2: {
                const now = new Date();
                return this.makeDate(now.getFullYear(), now.getMonth() + 1, n(0, 2));
            }

            // MDD or DMM (올해)
            case 3: {
                const year = new Date().getFullYear();
                const mdd = this.makeDate(year, n(0, 1), n(1, 2));
                const ddm = this.makeDate(year, n(2, 1), n(0, 2));
                if (mdd !== null && ddm !== null) return order.indexOf('d') < order.indexOf('m') ? ddm : mdd;
                return mdd ?? ddm;
            }

            // MMDD or DDMM (올해) or YYYY
            case 4: {
                const year = new Date().getFullYear();
                const md = this.makeDate(year, n(0, 2), n(2, 2));
                const dm = this.makeDate(year, n(2, 2), n(0, 2));
                if (md !== null && dm !== null) return order.indexOf('d') < order.indexOf('m') ? dm : md;
                return md ?? dm ?? this.makeDate(n(0, 4), 1, 1);
            }

            // YYMMDD or YYYYMM
            case 6:
                return (
                    this.makeDate(this.toFullYear(n(0, 2)), n(2, 2), n(4, 2)) ??
                    this.makeDate(n(0, 4), n(4, 2), 1) ??
                    (order[0] === 'd'
                        ? this.makeDate(this.toFullYear(n(4, 2)), n(2, 2), n(0, 2))
                        : this.makeDate(this.toFullYear(n(4, 2)), n(0, 2), n(2, 2)))
                );

            // YYYYMMDD or DDMMYYYY or MMDDYYYY
            case 8: {
                const ymd = this.makeDate(n(0, 4), n(4, 2), n(6, 2));
                if (ymd !== null) return ymd;

                const dmy = this.makeDate(n(4, 4), n(2, 2), n(0, 2));
                const mdy = this.makeDate(n(4, 4), n(0, 2), n(2, 2));
                if (dmy !== null && mdy !== null) return order[0] === 'd' ? dmy : mdy;
                return dmy ?? mdy;
            }

            // unix time stamp (초)
            case 10:
                return new Date(Number(digits) * 1000);

            // YYYYMMDDHHmm
            case 12:
                return this.makeDate(n(0, 4), n(4, 2), n(6, 2), [n(8, 2), n(10, 2)]);

            // unix time stamp (밀리초)
            case 13:
                return new Date(Number(digits));

            // YYYYMMDDHHmmss
            case 14:
                return this.makeDate(n(0, 4), n(4, 2), n(6, 2), [n(8, 2), n(10, 2), n(12, 2)]);
        }

        return null;
    }

    /**
     * 문자열 형태의 초기날짜를 최대한 해석한다.
     *
     * 2020-10-10, 2020.10.10, 2020/10/10, 20201010, 201010, 2020년 10월 10일, 2020年10月10日,
     * 10-12-2020 (언어설정 및 값의 범위로 일/월 순서 판단), Oct 10, 2020, 10 October 2020,
     * 2020-10-10 13:20:10, 2020.10.10 오후 1시 20분 등
     *
     * @param value - 초기날짜
     * @returns Date 객체
     */
    parseString(value: string): Date {
        const text = value.trim();
        if (text.length === 0) return new Date(NaN);

        // 숫자만 있는 경우
        if (/^\d+$/.test(text) === true) {
            return this.parseDigits(text) ?? new Date(NaN);
        }

        // 시간대 정보가 포함된 ISO 8601 형식은 브라우저 해석을 따른다.
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/i.test(text) === true) {
            return new Date(text);
        }

        const date = this.parseText(text);
        if (date !== null) return date;

        // 해석에 실패한 경우 문자가 포함되어 있을 때에만 브라우저 해석을 따른다. (2020.02.30 등이 자동보정되는 것을 방지)
        return /[a-z]/i.test(text) === true ? new Date(text) : new Date(NaN);
    }

    /**
     * 구분자가 포함된 날짜 문자열을 해석한다.
     *
     * @param text - 날짜 문자열
     * @returns Date 객체 (해석할 수 없는 경우 null)
     */
    parseText(text: string): Date {
        let source = text.toLowerCase();

        /**
         * 시간 해석
         */
        const time = [0, 0, 0, 0];
        let meridiem: 'am' | 'pm' = null;

        const meridiemMatch = source.match(/(a\.?m\.?|p\.?m\.?|오전|오후|午前|午後|上午|下午)/);
        if (meridiemMatch !== null) {
            meridiem = /^(a|오전|午前|上午)/.test(meridiemMatch[1]) === true ? 'am' : 'pm';
            source = source.replace(meridiemMatch[0], ' ');
        }

        const timeMatch =
            source.match(/(?:t|\s|^)(\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3})\d*)?)?/) ??
            source.match(/(\d{1,2})\s*(?:시|時|点|點)(?:\s*(\d{1,2})\s*(?:분|分))?(?:\s*(\d{1,2})\s*(?:초|秒))?/);
        if (timeMatch !== null) {
            time[0] = Number(timeMatch[1]);
            time[1] = Number(timeMatch[2] ?? 0);
            time[2] = Number(timeMatch[3] ?? 0);
            time[3] = Number((timeMatch[4] ?? '0').padEnd(3, '0'));
            source = source.replace(timeMatch[0], ' ');
        }

        if (meridiem === 'pm' && time[0] < 12) time[0] += 12;
        if (meridiem === 'am' && time[0] === 12) time[0] = 0;

        /**
         * 한/중/일 표기 (2020년 10월 10일, 2020年10月10日)
         */
        const cjk = source.match(/(?:(\d{2,4})\s*[년年])?\s*(\d{1,2})\s*[월月](?:\s*(\d{1,2})\s*[일日号號]?)?/);
        if (cjk !== null) {
            const year = cjk[1] ? this.toFullYear(Number(cjk[1])) : new Date().getFullYear();
            return this.makeDate(year, Number(cjk[2]), Number(cjk[3] ?? 1), time);
        }

        /**
         * 영문 월 이름 (Oct 10, 2020 / 10 October 2020)
         */
        let month: number = null;
        const monthMatch = source.match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?/);
        if (monthMatch !== null) {
            month = FormatDate.MONTH_NAMES.indexOf(monthMatch[1]) + 1;
            source = source.replace(monthMatch[0], ' ');
        }

        // 요일 등 나머지 문자를 제거하고 숫자 덩어리만 추출한다.
        const numbers: string[] = source.match(/\d+/g) ?? [];
        if (numbers.length === 0) return null;

        if (month !== null) {
            if (numbers.length === 1) {
                return numbers[0].length === 4
                    ? this.makeDate(Number(numbers[0]), month, 1, time)
                    : this.makeDate(new Date().getFullYear(), month, Number(numbers[0]), time);
            }

            const yearIndex = numbers.findIndex((n) => n.length >= 3 || Number(n) > 31);
            const yi = yearIndex === -1 ? numbers.length - 1 : yearIndex;
            const year = this.toFullYear(Number(numbers[yi]));
            const day = Number(numbers[yi === 0 ? 1 : 0]);
            return this.makeDate(year, month, day, time);
        }

        // 날짜와 시간 사이 구분자가 없는 경우 (20201010 1230 등)
        if (numbers.length === 1 || (numbers.length === 2 && numbers[0].length >= 6)) {
            const date = this.parseDigits(numbers[0]);
            if (date === null) return null;
            if (numbers.length === 2 && timeMatch === null && /^\d{4}(\d{2})?$/.test(numbers[1]) === true) {
                date.setHours(Number(numbers[1].substring(0, 2)), Number(numbers[1].substring(2, 4)));
                date.setSeconds(Number(numbers[1].substring(4, 6) || 0));
            } else if (timeMatch !== null) {
                date.setHours(time[0], time[1], time[2], time[3]);
            }
            return date;
        }

        const values = numbers.slice(0, 3).map(Number);
        const lengths = numbers.slice(0, 3).map((n) => n.length);
        const order = this.getDateOrder();

        // 2개 : 년-월 또는 월-일
        if (values.length === 2) {
            const [a, b] = values;
            if (lengths[0] === 4) return this.makeDate(a, b, 1, time);
            if (lengths[1] === 4) return this.makeDate(b, a, 1, time);

            const year = new Date().getFullYear();
            const md = this.makeDate(year, a, b, time);
            const dm = this.makeDate(year, b, a, time);
            if (md !== null && dm !== null) return order.indexOf('d') < order.indexOf('m') ? dm : md;
            return md ?? dm;
        }

        const [a, b, c] = values;
        const candidates: Date[] = [];

        if (lengths[0] >= 3 || a > 31) {
            // 년도가 앞에 오는 경우 : Y-M-D 우선, 불가능할 경우 Y-D-M
            candidates.push(this.makeDate(this.toFullYear(a), b, c, time));
            candidates.push(this.makeDate(this.toFullYear(a), c, b, time));
        } else if (lengths[2] >= 3 || c > 31) {
            // 년도가 뒤에 오는 경우 : 언어설정에 따라 D-M-Y 또는 M-D-Y
            const dmy = this.makeDate(this.toFullYear(c), b, a, time);
            const mdy = this.makeDate(this.toFullYear(c), a, b, time);
            if (order[0] === 'd') candidates.push(dmy, mdy);
            else candidates.push(mdy, dmy);
        } else {
            // 모두 2자리 이하인 경우 : 언어설정 순서를 우선으로 가능한 조합을 찾는다.
            const orders = [order.join(''), 'ymd', 'dmy', 'mdy'];
            for (const o of orders) {
                const map = { [o[0]]: a, [o[1]]: b, [o[2]]: c };
                candidates.push(this.makeDate(this.toFullYear(map.y), map.m, map.d, time));
            }
        }

        return candidates.find((date) => date !== null) ?? null;
    }

    /**
     * 언어를 가져온다.
     *
     * @returns 2자리 언어코드
     */
    getLanguage(): string {
        if (this.language === null) {
            this.language = document.documentElement.lang;
        }

        return this.language;
    }

    /**
     * 언어를 가져온다.
     * 포맷함수가 호출되기전에 정의되어야 한다.
     *
     * @param language - 2자리 언어코드
     * @returns FormatDate
     */
    setLanguage(language: string): FormatDate {
        this.language = language;

        return this;
    }

    /**
     * unix time stamp 를 밀리초로 변환한다.
     * 1e11 미만인 경우 PHP 의 unix time stamp(초) 로 판단한다.
     *
     * @param num - unix time stamp
     * @returns 밀리초 단위의 unix time stamp
     */
    toMilliseconds(num: number): number {
        return Math.abs(num) < 1e11 ? num * 1000 : num;
    }

    /**
     * @deprecated toMilliseconds 를 사용한다.
     *
     * @param num - unix time stamp
     * @returns 밀리초 단위의 unix time stamp
     */
    to13Digits(num: number): number {
        return this.toMilliseconds(num);
    }

    /**
     * 날짜 포맷
     *
     * @param format - 포맷 형식
     * @returns 포맷팅된 날짜
     */
    format(format: string): string {
        /*
        if (format === 'w') {
            return String(this.getDay());
        }
        */

        const getISOWeek = (d: Date): string => {
            const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
            date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
            const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
            const week = Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
            return String(week).padStart(2, '0');
        };

        const formatMap = {
            Y: { year: 'numeric' }, // 2025
            E: { year: 'numeric' }, // 2025년, 2025年
            y: { year: '2-digit' }, // 25
            m: { month: '2-digit' }, // 01 ~ 12
            n: { month: 'numeric' }, // 1 ~ 12
            F: { month: 'long' }, // 3월, March
            M: { month: 'short' }, // Mar
            d: { day: '2-digit' }, // 01 ~ 31
            j: { day: 'numeric' }, // 1 ~ 31
            J: { day: 'numeric' }, // 1일 ~ 31일
            D: { weekday: 'short' }, // 월, Mon, 月
            l: { weekday: 'long' }, // 월요일, Monday, 月曜日
            h: { hour: '2-digit', hourCycle: 'h12' }, // 01 ~ 12
            g: { hour: 'numeric', hourCycle: 'h12' }, // 1 ~ 12
            H: { hour: '2-digit', hourCycle: 'h24' }, // 01 ~ 24
            G: { hour: 'numeric', hourCycle: 'h24' }, // 1 ~ 24
            i: { minute: '2-digit' }, // 00 ~ 59
            s: { second: '2-digit' }, // 00 ~ 59
        };

        const parts = format.match(/(Y|y|m|n|F|M|d|j|J|W|Q|R|D|l|h|g|H|G|i|s|\D|\.|\-|\s|\/|,|\(|\))/g);
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const result = parts.map((part) => {
            if (part === 'W') {
                return getISOWeek(this.date);
            }

            if (part === 'Q') {
                return String(Math.ceil((this.date.getMonth() + 1) / 3));
            }

            if (part === 'R') {
                return this.date.getMonth() < 6 ? '1' : '2';
            }

            if (formatMap[part]) {
                let value = new Intl.DateTimeFormat(this.getLanguage(), {
                    ...formatMap[part],
                    timeZone,
                }).format(this.date);

                if (part !== 'F' && part !== 'M' && part !== 'E' && part !== 'J') {
                    value = value.replace(/(\D+)?(\d+)(\D+)/, '$2');
                }

                if (part === 'i' || part === 's') {
                    return value.padStart(2, '0');
                }
                return value;
            } else {
                return part;
            }
        });

        return result.join('');
    }

    /**
     * 날짜를 추가하여 날짜를 변경한다.
     *
     * @param unit - 변경할 unit(y, m, w, d)
     * @param value - 추가할 값
     * @returns FormatDate
     */
    add(unit: string, value: number): FormatDate {
        if (unit === 'y') {
            this.setYear(this.getYear() + value);
        } else if (unit === 'm') {
            this.setMonth(this.getMonth() + value);
        } else if (unit === 'w') {
            this.setDate(this.getDate() + value * 7);
        } else if (unit === 'd') {
            this.setDate(this.getDate() + value);
        }

        return this;
    }

    /**
     * 날짜를 변경한다.
     *
     * @param unit - 변경할 unit(y, m, d)
     * @param value - 변경할 값
     * @returns FormatDate
     */
    set(unit: string, value: number): FormatDate {
        if (unit === 'y') {
            this.setYear(value);
        } else if (unit === 'm') {
            this.setMonth(value - 1); // 1월은 0, 12월은 11
        } else if (unit === 'd') {
            this.setDate(value);
        }

        return this;
    }

    /**
     * 년도를 설정한다. (alias setFullYear)
     *
     * @param year - 년도
     * @returns FormatDate
     */
    setYear(year: number): FormatDate {
        this.date.setFullYear(year);
        return this;
    }

    /**
     * 월을 설정한다.
     * 기본 자바스크립트 Month 관련 함수는 1이 2월이지만, 여기서는 1은 1월을 의미한다.
     *
     * @param month - 월
     * @returns FormatDate
     */
    setMonth(month: number): FormatDate {
        this.date.setMonth(month - 1);
        return this;
    }

    /**
     * 일을 설정한다.
     *
     * @param date - 일
     * @returns FormatDate
     */
    setDate(date: number): FormatDate {
        this.date.setDate(date);
        return this;
    }

    /**
     * 년도를 가져온다. (alias getFullYear)
     *
     * @returns 년도
     */
    getYear(): number {
        return this.date.getFullYear();
    }

    /**
     * 월을 가져온다.
     * 기본 자바스크립트 Month 관련 함수에서는 1이 2월이지만, 여기서는 1은 1월을 의미한다.
     *
     * @returns 월
     */
    getMonth(): number {
        return this.date.getMonth() + 1;
    }

    /**
     * 일을 가져온다.
     *
     * @returns 일
     */
    getDate(): number {
        return this.date.getDate();
    }

    /**
     * 요일을 가져온다.
     *
     * @returns 요일 (0 : 일요일, 6:토요일)
     */
    getDay(): number {
        return this.date.getDay();
    }

    /**
     * unixtime을 가져온다.
     *
     * @returns unixtime
     */
    getTime(): number {
        return this.date.getTime();
    }

    /**
     * 년도를 추가하여 날짜를 변경한다.
     *
     * @param {number} value - 추가할 값
     * @returns Date
     */
    addYear(value: number): FormatDate {
        return this.add('y', value);
    }

    /**
     * 월을 추가하여 날짜를 변경한다.
     *
     * @param {number} value - 추가할 값
     * @returns Date
     */
    addMonth(value: number): FormatDate {
        return this.add('m', value);
    }

    /**
     * 일을 추가하여 날짜를 변경한다.
     *
     * @param {number} value - 추가할 값
     * @returns Date
     */
    addDate(value: number): FormatDate {
        return this.add('d', value);
    }

    /**
     * 현재월의 첫번째 날짜를 가져온다.
     *
     * @returns 현재 월의 첫번째 날짜
     */
    getFirstDateOfMonth(): FormatDate {
        return this.clone().setDate(1);
    }

    /**
     * 현재월의 마지막 날짜를 가져온다.
     *
     * @returns 현재 월의 마지막 날짜
     */
    getLastDateOfMonth(): FormatDate {
        return this.clone()
            .setDate(1)
            .setMonth(this.getMonth() + 1)
            .setDate(0);
    }

    /**
     * 현재월의 첫번째 날짜를 가져온다.
     *
     * @returns 현재 월의 첫번째 날짜
     */
    getStartDateOfWeek(): FormatDate {
        return this.clone().setDate(this.getDate() - this.getDay());
    }

    /**
     * 현재월의 첫번째 날짜를 가져온다.
     *
     * @returns 현재 월의 첫번째 날짜
     */
    getEndDateOfWeek(): FormatDate {
        return this.clone().setDate(this.getDate() + 6 - this.getDay());
    }

    /**
     * 주어진 단위의 시작 시점으로 날짜를 변경한다.
     *
     * @param unit - 단위 (y: 년도, m: 월, w: 주, d: 일)
     * @returns FormatDate
     */
    startOf(unit: 'y' | 'm' | 'w' | 'd'): FormatDate {
        if (unit === 'y') {
            this.date.setMonth(0, 1);
            this.date.setHours(0, 0, 0, 0);
        } else if (unit === 'm') {
            this.date.setDate(1);
            this.date.setHours(0, 0, 0, 0);
        } else if (unit === 'w') {
            // ISO 8601: 월요일 시작 (일요일=0 → 7로 처리)
            const day = this.date.getDay() || 7;
            this.date.setDate(this.date.getDate() - (day - 1));
            this.date.setHours(0, 0, 0, 0);
        } else if (unit === 'd') {
            this.date.setHours(0, 0, 0, 0);
        }

        return this;
    }

    /**
     * 주어진 단위의 종료 시점으로 날짜를 변경한다.
     *
     * @param unit - 단위 (y: 년도, m: 월, w: 주, d: 일)
     * @returns FormatDate
     */
    endOf(unit: 'y' | 'm' | 'w' | 'd'): FormatDate {
        if (unit === 'y') {
            this.date.setMonth(11, 31);
            this.date.setHours(23, 59, 59, 999);
        } else if (unit === 'm') {
            this.date.setDate(1);
            this.date.setMonth(this.date.getMonth() + 1);
            this.date.setDate(0);
            this.date.setHours(23, 59, 59, 999);
        } else if (unit === 'w') {
            // ISO 8601: 일요일 종료 (일요일=0 → 7로 처리)
            const day = this.date.getDay() || 7;
            this.date.setDate(this.date.getDate() + (7 - day));
            this.date.setHours(23, 59, 59, 999);
        } else if (unit === 'd') {
            this.date.setHours(23, 59, 59, 999);
        }

        return this;
    }

    /**
     * 날짜가 올바른지 확인한다.
     *
     * @returns 올바른 날짜여부
     */
    isValid(): boolean {
        return isNaN(this.date.getTime()) === false;
    }

    /**
     * 현재 날짜가 주어진 날짜보다 이전인지 확인한다.
     *
     * @param target - 비교할 날짜
     * @returns 이전 여부
     */
    isBefore(target: FormatDate): boolean {
        return this.date.getTime() < target.date.getTime();
    }

    /**
     * 현재 날짜가 주어진 날짜보다 이후인지 확인한다.
     *
     * @param target - 비교할 날짜
     * @returns 이후 여부
     */
    isAfter(target: FormatDate): boolean {
        return this.date.getTime() > target.date.getTime();
    }

    /**
     * 주어진 날짜와의 일수 차이를 반환한다.
     *
     * @param target - 비교할 날짜
     * @returns 일수 차이 (양수: target이 이후, 음수: target이 이전)
     */
    diff(target: FormatDate): number {
        const msPerDay = 1000 * 60 * 60 * 24;
        const a = Date.UTC(this.getYear(), this.getMonth() - 1, this.getDate());
        const b = Date.UTC(target.getYear(), target.getMonth() - 1, target.getDate());
        return Math.round((b - a) / msPerDay);
    }

    /**
     * FormatDate를 복제한다.
     *
     * @returns Date
     */
    clone(): FormatDate {
        return new FormatDate(this.date).setLanguage(this.getLanguage());
    }
}
